/**
 * components/InfoHint/InfoHint.tsx — Reusable ⓘ button with tooltip or modal.
 *
 * Pulses for ~2s when pulsing prop is true. Parent feature subscribes to
 * useInfoHintFlash and passes pulsing state down. Used alongside surface
 * headings to provide contextual hints without cluttering the UI.
 *
 * When a `modal` prop is provided, clicking opens a Modal dialog instead of
 * the tooltip popover — used for page-level info on /team, /jobs, /activity.
 */

import { type ReactNode, useEffect, useState } from 'react';
import { Modal } from '@/components/Modal/Modal';
import styles from './InfoHint.module.css';

/** Structured tip section for use inside InfoHint modal bodies. */
export function InfoHintSection({
  icon,
  heading,
  children,
}: {
  icon: string;
  heading: string;
  children: ReactNode;
}) {
  return (
    <div className={styles.modalSection}>
      <div className={styles.modalSectionHeading}>
        <span className={styles.modalSectionIcon} aria-hidden="true">
          {icon}
        </span>
        <strong>{heading}</strong>
      </div>
      <p className={styles.modalSectionBody}>{children}</p>
    </div>
  );
}

/** Lead paragraph for use inside InfoHint modal bodies. */
export function InfoHintLead({ children }: { children: ReactNode }) {
  return <p className={styles.modalLead}>{children}</p>;
}

interface Props {
  label: string;
  tooltip: string;
  /** When provided, clicking opens a modal instead of the tooltip. */
  modal?: { title: string; body: ReactNode };
  pulsing?: boolean;
}

export function InfoHint({ label, tooltip, modal, pulsing: externalPulsing = false }: Props) {
  const [open, setOpen] = useState(false);
  const [pulsing, setPulsing] = useState(false);

  useEffect(() => {
    if (!externalPulsing) return;
    setPulsing(true);
    const id = setTimeout(() => setPulsing(false), 2000);
    return () => clearTimeout(id);
  }, [externalPulsing]);

  return (
    <span className={styles.wrap}>
      <button
        type="button"
        aria-label={`Info about ${label}`}
        className={pulsing ? styles.iconPulsing : styles.icon}
        onClick={() => setOpen((o) => !o)}
      >
        ⓘ
      </button>
      {modal ? (
        <Modal open={open} onOpenChange={setOpen} title={modal.title} size="md">
          {modal.body}
        </Modal>
      ) : open ? (
        <div role="tooltip" className={styles.tooltip}>
          {tooltip}
        </div>
      ) : null}
    </span>
  );
}
